import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PageHeader from '../Common/PageHeader/PageHeader';
import { APP_BASE_URL } from '../Chat/EmployeeDashboard/dashboardUrls';
import { updateDocumentTitle } from '../../utils/documentTitle';
import './AdminHubPage.css';

/**
 * AdminLifecycleEmailsPage: /admin/lifecycle-emails (Nitish, Sep 16 2026 16:32 ET, Aria's
 * session: "I should be able to access all the things I'm tracking from one place; it should
 * open inside the app"). The card on the Admin hub opens Aria's "Lifecycle Emails" board
 * (a registered employee dashboard, served like every other app at
 * /app/{employee_post_office_id}/{path}/) in an iframe, the same embed the Employee Computer's
 * DashboardApp uses: cc_theme for a first paint that matches the site, and a per-visit `v`
 * token so a visit never shows the browser's cached copy. Cell controls stay on
 * /admin/lifecycle; this page is the read.
 */
export const LIFECYCLE_EMAILS_BOARD = {
  employeeId: '6a6bb3296dac83c833ce5122', // Aria's workspace id (the board's app root)
  path: 'dashboard/lifecycle-emails',
  name: 'Lifecycle Emails',
};

export const lifecycleEmailsBoardUrl = (dark, rev) =>
  `${APP_BASE_URL}/app/${LIFECYCLE_EMAILS_BOARD.employeeId}/${LIFECYCLE_EMAILS_BOARD.path}/?cc_theme=${dark ? 'dark' : 'light'}&v=${rev}`;

const AdminLifecycleEmailsPage = () => {
  const { isDarkMode } = useTheme();
  // One revision token per visit: the src changes on every mount, so the board is fetched fresh
  // each time the card is opened (a theme flip re-navigates the iframe too).
  const rev = useMemo(() => Date.now(), []);
  const src = lifecycleEmailsBoardUrl(isDarkMode, rev);
  const fullPage = `${APP_BASE_URL}/app/${LIFECYCLE_EMAILS_BOARD.employeeId}/${LIFECYCLE_EMAILS_BOARD.path}/`;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  useEffect(() => { updateDocumentTitle('Lifecycle Emails'); }, []);

  return (
    <div className="adm-page adm-lce">
      <p className="adm-crumb"><Link to="/admin">Admin</Link> / Lifecycle Emails</p>
      <PageHeader
        title="Lifecycle Emails"
        subtitle="Every lifecycle email sent, who got it and what they did after; signups held by country. Live from production."
      />
      <p className="adm-stamp">
        <Link to="/admin/lifecycle">Cell controls</Link>
        <a href={fullPage} target="_blank" rel="noopener noreferrer">Open full page</a>
        {!loaded && <span>Loading the board…</span>}
      </p>
      <div className="adm-embed">
        <iframe
          className="adm-embed-frame"
          title={LIFECYCLE_EMAILS_BOARD.name}
          src={src}
          onLoad={() => setLoaded(true)}
          data-testid="adm-lce-frame"
        />
      </div>
    </div>
  );
};

export default AdminLifecycleEmailsPage;
